import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useAuth } from "../auth/AuthContext";

// Backend user shape (partial, extend as needed)
export interface BackendUser {
  id: string;
  email: string;
  is_internal?: boolean;
  demography_ids?: string[];
  submission_limit?: number;
  submission_count?: number;
  payment_contact_id?: string | null;
  created_at?: string;
  updated_at?: string;
}

interface UserContextType {
  backendUser: BackendUser | null;
  isLoading: boolean;
  error: string | null;
  refresh: (email?: string) => Promise<void>;
  setBackendUser: React.Dispatch<React.SetStateAction<BackendUser | null>>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // @ts-ignore
  const { user } = useAuth();
  const [backendUser, setBackendUser] = useState<BackendUser | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(
    async (emailParam?: string) => {
      const accessToken = user?.accessToken as string | undefined;
      const email = emailParam || (user?.email as string | undefined);
      if (!accessToken || !email) {
        return; // silently ignore until auth is ready
      }
      setIsLoading(true);
      setError(null);
      try {
        const apiUrl = process.env.REACT_APP_API_URL as string;
        const url = `${apiUrl}/users/${encodeURIComponent(email)}/`;
        const res = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `bearer ${accessToken}`,
          },
        });
        if (!res.ok) {
          throw new Error(`Failed to fetch user (${res.status})`);
        }
        const data: BackendUser = await res.json();
        setBackendUser(data || null);
      } catch (e: any) {
        setError(e?.message || "Failed to fetch user");
        setBackendUser(null);
      } finally {
        setIsLoading(false);
      }
    },
    [user?.accessToken, user?.email]
  );

  const value = useMemo(
    () => ({ backendUser, isLoading, error, refresh, setBackendUser }),
    [backendUser, isLoading, error, refresh]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useBackendUser = (): UserContextType => {
  const ctx = useContext(UserContext);
  if (!ctx) {
    throw new Error("useBackendUser must be used within a UserProvider");
    }
  return ctx;
};
